import React, { useState, useMemo, useEffect } from "react";
import Papa from "papaparse";
import { Form, Input, Space, Button, message, Upload, Alert, Select, DatePicker, Radio } from "antd";
import { QuestionOutlined, ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons"
import { PageHeader } from "@ant-design/pro-layout";
import UploadCsv from "../../../../components/UploadCsv";
import useLdl from "../../../../hooks/useLdl";
import useAuth from "../../../../hooks/useAuth";
import useApp from "../../../../hooks/useApp";
import CopyToClipBoard from "../../../../components/CopyToClipBoard";
import type { UploadFile } from "antd/es/upload/interface";
import dayjs from "dayjs";

const { Option } = Select;
type Props = {};

const LeaseTransfer: React.FC<Props> = () => {
  const ldl = useLdl();
  const { user }: any = useAuth();
  const app: any = useApp();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [defaultFileList, setDefaultFileList] = useState<UploadFile<any>[]>([]);
  const [uploadDisabled, setUploadDisabled] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorList, setErrorList] = useState("");
  const disabled = useMemo(
    () =>
      !ldl.leaseTransferSchema.isValidSync(ldl.leaseTransfer) ||
      !ldl.leaseTransfer.file,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ldl.leaseTransfer]
  );

  useEffect(() => {
    if (errorList) message.error(errorList);
  }, [errorList]);

  const validateColumns = (results: any): boolean =>
    results.data.some((line: any, index: number) => {
      const notANumber = line[0] && !Number(line[0]);
      // if value is not a number
      if (notANumber)
        setErrorList(`Line ${index + 1}: [${line[0]}] is not a valid number`);
      return notANumber;
    });

  const beforeUpload = (file: any) => {
    setUploadDisabled(true);
    setErrorList("");
    if (file.type !== "text/csv") {
      message.error(`${file.name} is not a csv file`);
      setUploadDisabled(false);
      return Upload.LIST_IGNORE;
    }

    Papa.parse(file, {
      skipEmptyLines: true,
      delimiter: ",",
      complete: function (results) {
        // validates file is not empty
        if (!results.data.length) {
          message.error("File must not be empty");
          setDefaultFileList([]);
        }

        // validates file only has minimum number of columns (7)
        if (results.data.some((line: any) => line.length < 7)) {
          message.error("File should have at least seven columns");
          setDefaultFileList([]);
        }

        if (validateColumns(results)) {
          setUploadDisabled(false);
          setDefaultFileList([]);
          return;
        }

        ldl.actions.setLeaseTransfer({
          ...ldl.leaseTransfer,
          filename: file.name.split(".csv")[0].split(" ").join("_"),
          file: file,
          csv: Papa.unparse(results.data, { newline: "\n" }).replace(/"/g, ""),
        });

        setUploadDisabled(false);
      },
    });

    return false;
  };

  const onChange = (info: any) => {
    setDefaultFileList(info.fileList);
    if (!info.fileList.length) {
      ldl.actions.setLeaseTransfer({
        ...ldl.leaseTransfer,
        filename: "",
        file: null,
        csv: "",
      });
    }

    if (info.file.status === "done") {
      message.success(`${info.file.name} file uploaded successfully`);
    } else if (info.file.status === "error") {
      message.error(`${info.file.name} file upload failed.`);
    }
  };

  const getButtonStatus = (buttonName: string) => {
    if (ldl.leaseTransfer.targetLeaseType === buttonName) {
      return "primary";
    }
    return "default";
  }

  const getArrow = () => {
    if (ldl.leaseTransfer.targetLeaseType === "PSLOwned") {
      return <ArrowLeftOutlined style={{ fontSize: "20px", fontWeight: "bolder", margin: "1em" }} />;
    }
    else if (ldl.leaseTransfer.targetLeaseType === "LeasedTimeBound") {
      return <ArrowRightOutlined style={{ fontSize: "20px", fontWeight: "bolder", margin: "1em" }} />;
    }
    return <QuestionOutlined style={{ fontSize: "20px", fontWeight: "bolder", margin: "1em" }} />;
  }

  const getLeaseInfoDisabled = () => {
    return ldl.leaseTransfer.targetLeaseType !== "LeasedTimeBound";
  }

  return (
    <>
      <div className="site-page-header-ghost-wrapper">
        <PageHeader
          ghost={false}
          title="LDL"
          subTitle="Transfer Lease"
        />
        {successMessage && (
          <Space
            size={"large"}
            direction="horizontal"
            style={{ marginBottom: 20 }}
          >
            <Alert
              message={`The job ${successMessage} has been created.`}
              type="info"
            />
            <CopyToClipBoard text={successMessage} />
          </Space>
        )}
      </div>
      <div style={{ maxWidth: 524, marginLeft: "auto", marginRight: "auto" }}>
        <Space direction="vertical" size={"large"}>
          <Form name="basic" layout="vertical" form={form}>
            <Space direction="horizontal" size={"large"}>
              <Form.Item
                label="User Name"
                name="username"
                style={{ width: 250 }}
                initialValue={user?.usernameOG}
              >
                <Input disabled readOnly />
              </Form.Item>

              <Form.Item
                label="Request Date"
                name="requestDate"
                style={{ width: 250 }}
                initialValue={new Date().toISOString()}
              >
                <Input disabled readOnly />
              </Form.Item>
            </Space>

            <Form.Item label="File To Load" style={{ width: 500, textAlign: "center" }}>
              <UploadCsv
                beforeUpload={beforeUpload}
                onChange={onChange}
                defaultFileList={defaultFileList}
                setDefaultFileList={setDefaultFileList}
                disabled={uploadDisabled}
                setDisabled={setUploadDisabled}
              />
            </Form.Item>

            <Form.Item label="Source" name="sourceBU" style={{ width: 500, textAlign: "left" }}>
              <Radio.Group
                options={[
                  { value: "EOPS", label: "EOPS" },
                  { value: "Labs", label: "Labs" }
                ]}
                onChange={(e) =>
                  ldl.actions.setLeaseTransfer({
                    ...ldl.leaseTransfer,
                    sourceBU: e.target.value,
                  })
                }
                value={ldl.leaseTransfer.sourceBU}
                optionType="button"
                buttonStyle="solid"
              />
            </Form.Item>

            <Space direction="horizontal" size={"large"} style={{ textAlign: "center", width: "100%" }} >
              <Form.Item label="Convert Lease To...">
                <Button
                  type={getButtonStatus("PSLOwned")}
                  onClick={(e) => {
                    ldl.actions.setLeaseTransfer({ ...ldl.leaseTransfer, targetLeaseType: "PSLOwned" });
                  }}
                >PSLOwned</Button>
                {getArrow()}
                <Button
                  type={getButtonStatus("LeasedTimeBound")}
                  onClick={(e) => {
                    ldl.actions.setLeaseTransfer({ ...ldl.leaseTransfer, targetLeaseType: "LeasedTimeBound" });
                  }}
                >LeasedTimeBound</Button>
              </Form.Item>
            </Space>

            <Space direction="horizontal" size={"large"}>
              <Form.Item label="National ID Type" style={{ width: 250 }}>
                <Select
                  showSearch
                  placeholder="Select National ID type"
                  onChange={(value) =>
                    ldl.actions.setLeaseTransfer({
                      ...ldl.leaseTransfer,
                      nationalIDType: value,
                    })
                  }
                  value={ldl.leaseTransfer.nationalIDType}
                  disabled={getLeaseInfoDisabled()}
                >
                  {app.config?.NATIONAL_ID_TYPES?.map(
                    (type: string) => (
                      <Option key={type} value={type}>
                        {type}
                      </Option>
                    )
                  )}
                </Select>
              </Form.Item>

              <Form.Item label="From Vendor" style={{ width: 250 }}>
                <Select
                  showSearch
                  placeholder="Select Vendor"
                  onChange={(value) =>
                    ldl.actions.setLeaseTransfer({
                      ...ldl.leaseTransfer,
                      vendorPartyID: value,
                    })
                  }
                  value={ldl.leaseTransfer.vendorPartyID}
                  disabled={getLeaseInfoDisabled()}
                >
                  {app.config?.VENDORS?.map((vendor: any) => (
                    <Option key={vendor.partyid} value={vendor.partyid}>
                      {vendor.vendor}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Space>

            <Form.Item label="Expiration Date" style={{ width: 250 }}>
              <DatePicker
                style={{ width: "100%" }}
                format="MM-DD-YYYY"
                disabledDate={(current) => {
                  return dayjs() >= current;
                }}
                onChange={(date, dateString) => {
                  ldl.actions.setLeaseTransfer({
                    ...ldl.leaseTransfer,
                    expireDate: `${dateString}`,
                  });
                }}
                disabled={getLeaseInfoDisabled()}
                value={dayjs(ldl.leaseTransfer.expireDate)}
              />
            </Form.Item>

            <Form.Item>
              <Button
                className="button-primary"
                type="primary"
                onClick={() =>
                  ldl.actions.uploadLeaseTransfer({
                    setIsLoading,
                    form,
                    setDefaultFileList,
                    setDisabled: setUploadDisabled,
                    setSuccessMessage,
                  })
                }
                disabled={disabled}
                loading={isLoading}
                style={{ width: 250, marginTop: 30 }}
              >
                EXECUTE
              </Button>
            </Form.Item>
          </Form>
        </Space>
      </div>
    </>
  );
};

export default LeaseTransfer;
